import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plane, MapPin, Calendar, Users, ChevronLeft, ChevronRight, Pencil, Trash2, Send, CornerDownRight, MessageCircle, Target, X } from 'lucide-react';

// photos 배열 역호환: string → { url, caption }
const norm = p => typeof p === 'string' ? { url: p, caption: '' } : p;
import api from '../utils/api';
import dayjs from 'dayjs';

function CommentItem({ c, me, isSuperadmin, onDelete, onReply }) {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submitReply = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      await onReply(c.id, replyText.trim());
      setReplyText('');
      setShowReply(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-full bg-gray-700 flex-shrink-0 flex items-center justify-center text-xs text-gray-300 font-medium">
          {(c.author?.name || c.author?.username || '?')[0]}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-medium text-gray-300">{c.author?.name || c.author?.username}</span>
            <span className="text-[10px] text-gray-600">{dayjs(c.createdAt).format('MM.DD HH:mm')}</span>
          </div>
          <p className="text-sm text-gray-400 leading-relaxed">{c.content}</p>
          <div className="flex gap-3 mt-1">
            <button onClick={() => setShowReply(v => !v)}
              className="text-[11px] text-gray-600 hover:text-brand transition-colors">답글</button>
            {(c.authorId === me?.id || isSuperadmin) && (
              <button onClick={() => onDelete(c.id)}
                className="text-[11px] text-gray-700 hover:text-red-400 transition-colors">삭제</button>
            )}
          </div>
          {showReply && (
            <div className="mt-2 flex gap-2">
              <input
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && submitReply()}
                placeholder="답글 입력..."
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-brand placeholder-gray-600"
              />
              <button onClick={submitReply} disabled={submitting}
                className="px-3 py-1.5 bg-brand text-white text-xs rounded-lg hover:bg-brand/80 disabled:opacity-50">
                <Send size={12} />
              </button>
            </div>
          )}
        </div>
      </div>

      {c.replies?.filter(r => r.isActive).map(r => (
        <div key={r.id} className="flex items-start gap-3 ml-10">
          <CornerDownRight size={12} className="text-gray-700 flex-shrink-0 mt-1" />
          <div className="w-6 h-6 rounded-full bg-gray-700 flex-shrink-0 flex items-center justify-center text-[10px] text-gray-300">
            {(r.author?.name || r.author?.username || '?')[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-xs font-medium text-gray-300">{r.author?.name || r.author?.username}</span>
              <span className="text-[10px] text-gray-600">{dayjs(r.createdAt).format('MM.DD HH:mm')}</span>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">{r.content}</p>
            {(r.authorId === me?.id || isSuperadmin) && (
              <button onClick={() => onDelete(r.id)}
                className="text-[11px] text-gray-700 hover:text-red-400 mt-0.5">삭제</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function TripReportViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const me = JSON.parse(localStorage.getItem('mfg_user') || '{}');
  const isSuperadmin = me.role === 'superadmin';

  const [report, setReport] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { urls: [...], idx: 0 }

  const openLightbox = (photos, idx) => setLightbox({ photos, idx });
  const closeLightbox = () => setLightbox(null);
  const lightboxPrev = useCallback(() => setLightbox(lb => ({ ...lb, idx: (lb.idx - 1 + lb.photos.length) % lb.photos.length })), []);
  const lightboxNext = useCallback(() => setLightbox(lb => ({ ...lb, idx: (lb.idx + 1) % lb.photos.length })), []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = e => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft' && lightbox.photos.length > 1) lightboxPrev();
      if (e.key === 'ArrowRight' && lightbox.photos.length > 1) lightboxNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, lightboxPrev, lightboxNext]);

  const load = () => api.get(`/trips/${id}`).then(r => setReport(r.data));
  useEffect(() => { load(); }, [id]);

  const submitComment = async () => {
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/trips/${id}/comments`, { content: comment.trim() });
      setComment('');
      load();
    } finally {
      setSubmitting(false);
    }
  };

  const submitReply = async (parentId, text) => {
    await api.post(`/trips/${id}/comments`, { content: text, parentId });
    load();
  };

  const deleteComment = async commentId => {
    if (!confirm('댓글을 삭제하시겠습니까?')) return;
    await api.delete(`/trips/${id}/comments/${commentId}`);
    load();
  };

  const deleteReport = async () => {
    if (!confirm('출장보고를 삭제하시겠습니까?')) return;
    await api.delete(`/trips/${id}`);
    navigate('/trips');
  };

  if (!report) return (
    <div className="flex items-center justify-center flex-1 text-gray-600 text-sm">불러오는 중...</div>
  );

  const comments = (report.comments || []).filter(c => c.isActive);
  const isAuthor = report.authorId === me?.id;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* 상단 액션 바 */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <button onClick={() => navigate('/trips')}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors">
          <ChevronLeft size={16} /> 목록
        </button>
        {(isAuthor || isSuperadmin) && (
          <div className="flex gap-2">
            <button onClick={() => navigate(`/trips/${id}/edit`)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors">
              <Pencil size={12} /> 수정
            </button>
            <button onClick={deleteReport}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-gray-800 transition-colors">
              <Trash2 size={12} /> 삭제
            </button>
          </div>
        )}
      </div>

      {/* 본문: 좌우 2분할 풀-헤이트 */}
      <div className="flex gap-4 flex-1 min-h-0">

        {/* 좌측 패널: 출장 정보 + 댓글 */}
        <div className="w-80 flex-shrink-0 flex flex-col gap-4 overflow-y-auto">

          {/* 출장 정보 카드 */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Plane size={14} className="text-brand" />
              <span className="text-xs text-brand font-medium">출장보고</span>
            </div>
            <h1 className="text-lg font-bold text-white leading-snug">{report.title}</h1>

            <div className="border-t border-gray-800 pt-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-[10px] text-gray-300 font-medium flex-shrink-0">
                  {(report.author?.name || report.author?.username || '?')[0]}
                </div>
                <span className="text-sm text-gray-300 font-medium">{report.author?.name || report.author?.username}</span>
                <span className="text-[10px] text-gray-600 ml-auto">{dayjs(report.createdAt).format('YYYY.MM.DD')}</span>
              </div>

              {report.destination && (
                <div className="flex items-start gap-2 text-sm text-gray-400">
                  <MapPin size={13} className="text-gray-600 mt-0.5 flex-shrink-0" />
                  <span>{report.destination}</span>
                </div>
              )}

              {(report.startDate || report.endDate) && (
                <div className="flex items-start gap-2 text-sm text-gray-400">
                  <Calendar size={13} className="text-gray-600 mt-0.5 flex-shrink-0" />
                  <span>
                    {report.startDate && dayjs(report.startDate).format('YYYY.MM.DD')}
                    {report.endDate && report.endDate !== report.startDate && ` ~ ${dayjs(report.endDate).format('MM.DD')}`}
                  </span>
                </div>
              )}

              {report.attendees && (
                <div className="flex items-start gap-2 text-sm text-gray-400">
                  <Users size={13} className="text-gray-600 mt-0.5 flex-shrink-0" />
                  <span>{report.attendees}</span>
                </div>
              )}

              {report.purpose && (
                <div className="flex items-start gap-2 text-sm text-gray-400">
                  <Target size={13} className="text-gray-600 mt-0.5 flex-shrink-0" />
                  <span className="leading-relaxed whitespace-pre-wrap">{report.purpose}</span>
                </div>
              )}
            </div>
          </div>

          {/* 댓글 */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 flex flex-col gap-4">
            <h2 className="text-xs font-semibold text-gray-400 flex items-center gap-2">
              <MessageCircle size={13} /> 댓글
              {comments.length > 0 && <span className="text-gray-600">({comments.length})</span>}
            </h2>

            <div className="space-y-5">
              {comments.map(c => (
                <CommentItem
                  key={c.id}
                  c={c}
                  me={me}
                  isSuperadmin={isSuperadmin}
                  onDelete={deleteComment}
                  onReply={submitReply}
                />
              ))}
              {comments.length === 0 && (
                <p className="text-xs text-gray-600">아직 댓글이 없습니다.</p>
              )}
            </div>

            <div className="flex gap-2">
              <input
                value={comment}
                onChange={e => setComment(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && submitComment()}
                placeholder="댓글을 입력하세요..."
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand placeholder-gray-600"
              />
              <button onClick={submitComment} disabled={submitting || !comment.trim()}
                className="px-3 py-2 bg-brand text-white text-sm rounded-lg hover:bg-brand/80 disabled:opacity-50">
                <Send size={13} />
              </button>
            </div>
          </div>
        </div>

        {/* 우측 패널: 전체의견 + 사진 블록 */}
        <div className="flex-1 flex flex-col gap-6 overflow-y-auto min-h-0">

          {/* 전체 의견 */}
          {report.summary && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">전체 의견</h2>
              <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{report.summary}</p>
            </div>
          )}

          {/* 사진 블록들 */}
          {(report.blocks || []).length > 0 && (
            <div className="space-y-6">
              {(report.blocks || []).map((block, i) => (
                <div key={block.id || i} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                  {block.photos?.length > 0 && (() => {
                    const photos = block.photos.map(norm);
                    return (
                      <div className={`grid gap-2 mb-4 ${
                        photos.length === 1 ? 'grid-cols-1' :
                        photos.length === 2 ? 'grid-cols-2' :
                        photos.length <= 4 ? 'grid-cols-2 sm:grid-cols-4' :
                        'grid-cols-2 sm:grid-cols-3'
                      }`}>
                        {photos.map((photo, pIdx) => (
                          <div key={pIdx} className="relative group cursor-zoom-in overflow-hidden rounded-lg"
                            onClick={() => openLightbox(photos, pIdx)}>
                            <img
                              src={photo.url}
                              alt=""
                              className={`w-full object-cover ${photos.length === 1 ? 'max-h-96' : 'h-44'}`}
                            />
                            {photo.caption && (
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-2">
                                <p className="text-white text-xs leading-snug line-clamp-2">{photo.caption}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  {block.caption && (
                    <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap">{block.caption}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 사진/의견 없을 때 */}
          {!report.summary && (report.blocks || []).length === 0 && (
            <div className="flex-1 flex items-center justify-center text-gray-700 text-sm">
              내용이 없습니다.
            </div>
          )}
        </div>
      </div>

      {/* 라이트박스 */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/60 hover:text-white p-2">
            <X size={22} />
          </button>

          {lightbox.photos.length > 1 && (
            <>
              <button onClick={e => { e.stopPropagation(); lightboxPrev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-2">
                <ChevronLeft size={32} />
              </button>
              <button onClick={e => { e.stopPropagation(); lightboxNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-2">
                <ChevronRight size={32} />
              </button>
            </>
          )}

          <div className="flex flex-col items-center gap-4 max-w-[90vw]" onClick={e => e.stopPropagation()}>
            <img
              src={lightbox.photos[lightbox.idx].url}
              alt=""
              className="max-h-[80vh] max-w-[90vw] object-contain rounded-lg"
            />
            {lightbox.photos[lightbox.idx].caption && (
              <p className="text-white/80 text-sm text-center leading-relaxed max-w-xl px-4">
                {lightbox.photos[lightbox.idx].caption}
              </p>
            )}
          </div>

          {lightbox.photos.length > 1 && (
            <div className="absolute bottom-4 flex gap-1.5">
              {lightbox.photos.map((_, i) => (
                <button key={i}
                  onClick={e => { e.stopPropagation(); setLightbox(lb => ({ ...lb, idx: i })); }}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${i === lightbox.idx ? 'bg-white' : 'bg-white/30'}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
